import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Upload, 
  X, 
  Image as ImageIcon, 
  Loader2, 
  ExternalLink 
} from 'lucide-react';
import { toast } from 'sonner';
import { validateImageFile, generateImagePreview } from '@/services/uploadService';

interface ImageUploadProps {
  label: string;
  value: string;
  onChange: (url: string) => void;
  onUpload?: (file: File) => Promise<{ url: string }>;
  placeholder?: string;
  description?: string;
  previewSize?: 'small' | 'medium' | 'large';
  acceptedFormats?: string[];
  maxSize?: number; // em MB
  uploadOnly?: boolean; // Se true, só mostra upload, não input de URL
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  label,
  value,
  onChange,
  onUpload,
  placeholder = "https://exemplo.com/imagem.jpg",
  description,
  previewSize = 'medium',
  acceptedFormats = ['JPG', 'PNG', 'GIF', 'WebP'],
  maxSize = 5,
  uploadOnly = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const previewSizeClasses = {
    small: 'h-20 w-20',
    medium: 'h-32 w-full',
    large: 'h-40 w-full'
  };

  const handleFileSelect = async (file: File) => {
    // Validar arquivo
    const validation = validateImageFile(file);
    if (validation) {
      toast.error(validation);
      return;
    }

    setUploading(true);
    try {
      // Gerar preview
      const previewUrl = await generateImagePreview(file);
      setPreview(previewUrl);

      // Fazer upload se função fornecida
      if (onUpload) {
        const result = await onUpload(file);
        onChange(result.url);
        toast.success('Imagem enviada com sucesso!');
      }
    } catch (error) {
      console.error('Erro no upload:', error);
      toast.error('Erro ao enviar imagem');
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const removeImage = () => {
    onChange('');
    setPreview(null);
  };

  const currentImageUrl = preview || value;

  return (
    <div className="space-y-3">
      <Label className="text-sm font-extrabold">{label}</Label>
      
      {/* Input de URL - só mostra se não for uploadOnly */}
      {!uploadOnly && (
        <div className="flex gap-2">
          <Input
            type="url"
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="flex-1"
          />
          {value && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => window.open(value, '_blank')}
              title="Abrir link"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      {/* Área de Upload */}
      {onUpload && (
        <div className="space-y-2">
          {!uploadOnly && <div className="text-xs text-muted-foreground text-center">ou</div>}
          
          <div
            className={`
              border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
              ${dragOver 
                ? 'border-primary bg-primary/5' 
                : 'border-gray-300 hover:border-gray-400'
              }
              ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={!uploading ? openFileDialog : undefined}
          >
            {uploading ? (
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Enviando...</span>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="h-6 w-6 mx-auto text-gray-400" />
                <div className="text-sm">
                  <span className="font-extrabold text-primary">Clique para enviar</span>
                  <span className="text-muted-foreground"> ou arraste aqui</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {acceptedFormats.join(', ')} • Máx {maxSize}MB
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Input de arquivo oculto */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFileSelect(file);
        }}
        className="hidden"
      />

      {/* Preview da imagem */}
      {currentImageUrl && (
        <div className="relative">
          <div className={`
            border rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center
            ${previewSizeClasses[previewSize]}
          `}>
            <img
              src={currentImageUrl}
              alt="Preview"
              className="max-w-full max-h-full object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling!.style.display = 'flex';
              }}
            />
            <div className="hidden items-center justify-center h-full text-gray-400">
              <ImageIcon className="h-8 w-8" />
            </div>
          </div>
          
          {/* Botão de remover */}
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
            onClick={removeImage}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Descrição */}
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  );
};

export default ImageUpload;
